/**
 * Warehouse Specifikace fields (`warehouse_profile`) for doklady.
 * Empty values stay hidden — show only filled IČO / firma / adresa.
 */

export interface WarehouseSpecFields {
  name?: string | null;
  address?: string | null;
  responsibleCompanyName?: string | null;
  responsibleCompanyIco?: string | null;
  responsibleCompanyAddress?: string | null;
}

export interface WarehouseSpecLine {
  label: string;
  value: string;
}

export interface WarehouseSpecParty {
  name: string | null;
  ico: string | null;
  address: string | null;
}

function trimOrNull(raw: string | null | undefined): string | null {
  const t = String(raw ?? '').trim();
  return t ? t : null;
}

/** Firma / IČO / adresa — jen vyplněná pole ze specifikace skladu. */
export function warehouseSpecLines(
  wh: WarehouseSpecFields | null | undefined,
): WarehouseSpecLine[] {
  if (!wh) return [];
  const companyName = trimOrNull(wh.responsibleCompanyName);
  const ico = trimOrNull(wh.responsibleCompanyIco);
  const companyAddress = trimOrNull(wh.responsibleCompanyAddress);
  const warehouseAddress = trimOrNull(wh.address);
  const lines: WarehouseSpecLine[] = [];
  if (companyName) lines.push({ label: 'Firma', value: companyName });
  if (ico) lines.push({ label: 'IČO', value: ico });
  if (companyAddress && warehouseAddress && companyAddress !== warehouseAddress) {
    lines.push({ label: 'Adresa firmy', value: companyAddress });
    lines.push({ label: 'Adresa skladu', value: warehouseAddress });
  } else {
    const address = companyAddress || warehouseAddress;
    if (address) lines.push({ label: 'Adresa', value: address });
  }
  return lines;
}

export function warehouseSpecHasFields(
  wh: WarehouseSpecFields | null | undefined,
): boolean {
  return warehouseSpecLines(wh).length > 0;
}

/** Compact one-liner (IČO · adresa) for mobile captions. */
export function warehouseSpecCaption(
  wh: WarehouseSpecFields | null | undefined,
): string | null {
  const parts = warehouseSpecLines(wh).map((line) =>
    line.label === 'IČO' ? `IČO ${line.value}` : line.value,
  );
  return parts.length ? parts.join(' · ') : null;
}

/**
 * Legal party from specifikace: firma, IČO, adresa firmy (else adresa skladu).
 * Name falls back to warehouse name when firma is empty.
 */
export function warehouseSpecParty(
  wh: WarehouseSpecFields | null | undefined,
): WarehouseSpecParty {
  if (!wh) return { name: null, ico: null, address: null };
  return {
    name: trimOrNull(wh.responsibleCompanyName) || trimOrNull(wh.name),
    ico: trimOrNull(wh.responsibleCompanyIco),
    address: trimOrNull(wh.responsibleCompanyAddress) || trimOrNull(wh.address),
  };
}

/** true = specifikace má firmu / IČO / adresu (ne jen název skladu). */
export function warehouseSpecHasParty(
  wh: WarehouseSpecFields | null | undefined,
): boolean {
  if (!wh) return false;
  return !!(
    trimOrNull(wh.responsibleCompanyName) ||
    trimOrNull(wh.responsibleCompanyIco) ||
    trimOrNull(wh.responsibleCompanyAddress) ||
    trimOrNull(wh.address)
  );
}

export function warehousePdfParty(
  wh: WarehouseSpecFields | null | undefined,
  label: string,
  opts?: { preferWarehouseName?: boolean },
): WarehouseSpecParty & { label: string } {
  const party = warehouseSpecParty(wh);
  const warehouseName = trimOrNull(wh?.name);
  return {
    label,
    name: opts?.preferWarehouseName ? warehouseName || party.name : party.name,
    ico: party.ico,
    address: party.address,
  };
}

/** Fill blank party fields from specifikace; never overwrite filled values. */
export function fillPartyFromWarehouseSpec(
  existing: {
    name?: string | null;
    ico?: string | null;
    address?: string | null;
  },
  wh: WarehouseSpecFields | null | undefined,
): WarehouseSpecParty {
  const spec = warehouseSpecParty(wh);
  return {
    name: trimOrNull(existing.name) || spec.name,
    ico: trimOrNull(existing.ico) || spec.ico,
    address: trimOrNull(existing.address) || spec.address,
  };
}
